export interface FlatFieldError {
  /** Dotted react-hook-form path, e.g. `tips.0.items.2.answer`. */
  path: string;
  message: string;
}

interface FlattenOptions {
  /**
   * Skip leaves the API produced (`useEditPage` mirrors them onto the form with `type: "server"`).
   * `ValidationErrorBanner` sets this because `ErrorBanner` already lists the same 400 — without
   * it every server rejection would be printed twice, in two banners.
   */
  skipServer?: boolean;
}

/**
 * Walk the nested react-hook-form error tree and collect every leaf that carries a message.
 *
 * Callers must not assume an error sits on the field's ROOT key. The API keys its field errors
 * by the full issue path, so a bad room row arrives as `hotelRooms.0.name` — code that only read
 * `errors.hotelRooms.message` (the shape Zod's `flatten()` used to produce) silently showed
 * nothing. Flattening first makes a consumer work for both shapes.
 */
export function flattenFieldErrors(
  errors: unknown,
  { skipServer = false }: FlattenOptions = {},
  prefix = "",
  out: FlatFieldError[] = [],
): FlatFieldError[] {
  if (!errors || typeof errors !== "object") return out;
  const node = errors as Record<string, unknown>;
  if (typeof node.message === "string" && node.message) {
    if (!skipServer || node.type !== "server") out.push({ path: prefix, message: node.message });
    return out;
  }
  for (const [key, value] of Object.entries(node)) {
    if (key === "ref" || key === "types") continue;
    flattenFieldErrors(value, { skipServer }, prefix ? `${prefix}.${key}` : key, out);
  }
  return out;
}

/**
 * First message anywhere under one field — for inline slots that print a single line per field
 * (a tab's catalogue editor) and cannot render a per-row error of their own.
 *
 * Takes `unknown` on purpose: react-hook-form types an array field's error branch as a
 * `Merge<FieldError, …[]>` whose shape differs per field, and none of that matters to a walk
 * that only looks for `message` leaves.
 */
export function firstFieldError(branch: unknown): string | undefined {
  return flattenFieldErrors(branch)[0]?.message;
}
